import { z } from 'zod';
import { fieldSchema } from './fields.js';
import { authConfigSchema } from './auth.js';
export const baseConfigSchema = z.object({
    key: z.string(),
    title: z.string().optional(),
    fields: z.array(fieldSchema).optional()
});
// Build action variable schema (custom variable declarations)
export const buildActionVariableSchema = z.object({
    name: z.string(),
    value: z.string()
});
// Build action path replacement schema (for WSL path mapping, etc.)
export const buildActionPathReplaceSchema = z.object({
    search: z.string(),
    replace: z.string()
});
// Build action command schema (platform-specific)
export const buildActionCommandSchema = z.object({
    command: z.string(),
    args: z.array(z.string()).optional(),
    file_path_replace: z.array(buildActionPathReplaceSchema).optional(),
    document_path_replace: z.array(buildActionPathReplaceSchema).optional(),
    site_path_replace: z.array(buildActionPathReplaceSchema).optional()
});
// Build action execute dictionary (contains windows/unix commands)
export const buildActionExecuteSchema = z.object({
    windows: buildActionCommandSchema,
    unix: buildActionCommandSchema,
    variables: z.array(buildActionVariableSchema).optional(),
    stdout_type: z.string().optional()
});
// Build action schema (used in collections and singles)
export const buildActionSchema = z.object({
    key: z.string(),
    title: z.string().optional(),
    button_text: z.string(),
    execute: buildActionExecuteSchema
});
export const llmSettingsSchema = z.object({
    model: z.string(),
    temperature: z.number().optional()
});
export const promptItemConfigSchema = z.object({
    key: z.string(),
    title: z.string().optional(),
    llm_settings: llmSettingsSchema,
    savePromptHistory: z.boolean().optional(),
    saveResultHistory: z.boolean().optional(),
    showPreviewButton: z.boolean().optional(),
    description: z.string().optional(),
    _mergePartial: z.string().optional(),
    fields: z.array(fieldSchema).optional(),
});
export const singleConfigSchema = z.object({
    key: z.string(),
    title: z.string().optional(),
    file: z.string().optional(),
    dataformat: z.string().optional(),
    previewUrl: z.string().optional(),
    description: z.string().optional(),
    _mergePartial: z.string().optional(),
    hidePreviewIcon: z.boolean().optional(),
    hideExternalEditIcon: z.boolean().optional(),
    hideSaveButton: z.boolean().optional(),
    pullOuterRootKey: z.string().optional(),
    fields: z.array(fieldSchema).optional(),
    prompt_templates: z.array(z.string()).optional(),
    build_actions: z.array(buildActionSchema).optional(),
    // Accept camelCase variant from existing site models
    buildActions: z.array(buildActionSchema).optional(),
}).transform((data) => {
    // Normalize camelCase buildActions to snake_case build_actions
    const { buildActions, ...rest } = data;
    return { ...rest, build_actions: rest.build_actions ?? buildActions };
});
export const collectionConfigSchema = z.object({
    key: z.string(),
    title: z.string().optional(),
    folder: z.string(),
    extension: z.string(),
    dataformat: z.string(),
    itemtitle: z.string(),
    hideIndex: z.boolean().optional(),
    previewUrlBase: z.string().optional(),
    _mergePartial: z.string().optional(),
    sortkey: z.string().optional(),
    hidePreviewIcon: z.boolean().optional(),
    fields: z.array(fieldSchema),
    prompt_templates: z.array(z.string()).optional(),
    build_actions: z.array(buildActionSchema).optional(),
    // Accept camelCase variant from existing site models
    buildActions: z.array(buildActionSchema).optional(),
}).transform((data) => {
    // Normalize camelCase buildActions to snake_case build_actions
    const { buildActions, ...rest } = data;
    return { ...rest, build_actions: rest.build_actions ?? buildActions };
});
export const menuItemSchema = z.object({
    key: z.string()
});
export const menuSectionSchema = z.object({
    title: z.string(),
    key: z.string().optional(),
    matchRole: z.string().optional(),
    menuItems: z.array(menuItemSchema)
});
export const menuSchema = z.array(menuSectionSchema);
// Folder publish configuration
export const folderPublishConfSchema = z.object({
    type: z.literal('folder'),
    path: z.string(),
    publishScope: z.string(),
    overrideBaseURLSwitch: z.boolean(),
    overrideBaseURL: z.string()
});
// GitHub publish configuration
export const githubPublishConfSchema = z.object({
    type: z.literal('github'),
    title: z.string().optional(),
    username: z.string(),
    email: z.string(),
    repository: z.string(),
    branch: z.string(),
    deployPrivateKey: z.string(),
    deployPublicKey: z.string(),
    publishScope: z.string(),
    setGitHubActions: z.boolean(),
    keyPairBusy: z.boolean().optional(),
    overrideBaseURLSwitch: z.boolean(),
    overrideBaseURL: z.string(),
    pullOnly: z.boolean().optional(),
    backupAtPull: z.boolean().optional(),
    syncSelection: z.string().optional(),
    CNAMESwitch: z.boolean().optional(),
    CNAME: z.string().optional()
});
// System Git publish configuration
export const sysgitPublishConfSchema = z.object({
    type: z.literal('sysgit'),
    title: z.string().optional(),
    git_server_url: z.string(),
    email: z.string(),
    branch: z.string(),
    deployPrivateKey: z.string(),
    deployPublicKey: z.string(),
    publishScope: z.string(),
    setGitHubActions: z.boolean(),
    keyPairBusy: z.boolean().optional(),
    overrideBaseURLSwitch: z.boolean(),
    overrideBaseURL: z.string(),
    pullOnly: z.boolean().optional(),
    backupAtPull: z.boolean().optional(),
    syncSelection: z.string().optional(),
    CNAMESwitch: z.boolean().optional(),
    CNAME: z.string().optional()
});
// Git provider enum for CI configuration
export const gitProviderSchema = z.enum(['github', 'gitlab', 'forgejo', 'generic']);
// Generic Git publish configuration (supports GitHub, GitLab, Forgejo, etc.)
export const gitPublishConfSchema = z.object({
    type: z.literal('git'),
    title: z.string().optional(),
    gitProvider: gitProviderSchema, // Determines CI configuration
    gitBaseUrl: z.string(), // e.g., 'github.com', 'gitlab.com', 'localhost:3000'
    gitProtocol: z.enum(['ssh', 'https']),
    sshPort: z.number().optional(), // SSH port, defaults to 22 (only used for SSH protocol)
    username: z.string(),
    email: z.string(),
    repository: z.string(),
    branch: z.string(),
    deployPrivateKey: z.string(),
    deployPublicKey: z.string(),
    publishScope: z.string(),
    keyPairBusy: z.boolean().optional(),
    overrideBaseURLSwitch: z.boolean(),
    overrideBaseURL: z.string(),
    pullOnly: z.boolean().optional(),
    backupAtPull: z.boolean().optional(),
    syncSelection: z.string().optional(),
    // CI-related settings
    setCIWorkflow: z.boolean().optional(), // Whether to generate CI workflow files
    CNAMESwitch: z.boolean().optional(),
    CNAME: z.string().optional(),
});
// Union of all publish config types
export const publConfSchema = z.discriminatedUnion('type', [
    folderPublishConfSchema,
    githubPublishConfSchema,
    sysgitPublishConfSchema,
    gitPublishConfSchema
]);
export const siteConfigSchema = z.object({
    key: z.string(),
    name: z.string().optional(),
    source: z.object({
        type: z.literal('folder'),
        path: z.string()
    }).optional(),
    serve: z
        .object({
        key: z.string(),
        config: z.string(),
        hugoHidePreviewSite: z.boolean()
    })
        .optional(),
    build: z
        .object({
        key: z.string(),
        config: z.string()
    })
        .optional(),
    publish: z
        .array(z.object({
        key: z.string(),
        config: publConfSchema
    }))
        .optional(),
    tags: z.array(z.string()).optional(),
    template: z.boolean().optional(),
    remote: z.boolean().optional(),
    screenshotURL: z.string().optional(),
    homepageURL: z.string().optional(),
    importSiteURL: z.string().optional(),
    etalage: z.object({
        screenshots: z.array(z.string()).optional(),
        favicons: z.array(z.string()).optional()
    }).passthrough().optional(),
    lastPublish: z.number().optional(),
    publishStatus: z.number().int().min(0).max(8).optional(),
    lastEdit: z.number().optional(),
    transform: z.array(z.unknown()).optional()
}).transform((data) => ({
    ...data,
    // Ensure name always exists - use key as fallback
    name: data.name || data.key
}));
export const serveConfigSchema = z.object({
    key: z.string(),
    config: z.string(),
    hugoHidePreviewSite: z.boolean().optional()
});
export const buildConfigSchema = z.object({
    key: z.string(),
    config: z.string()
});
export const workspaceSchema = z.object({
    key: z.string(),
    path: z.string(),
    state: z.string()
});
// Base workspace details schema (for extending)
export const workspaceDetailsBaseSchema = z.object({
    ssgType: z.string().default('hugo'),
    ssgVersion: z.string(),
    serve: z.array(serveConfigSchema).optional(),
    build: z.array(buildConfigSchema).optional(),
    menu: menuSchema.optional(),
    // Collections and singles can be missing or empty - defaults to empty array
    collections: z.array(collectionConfigSchema).optional().default([]),
    singles: z.array(singleConfigSchema).optional().default([]),
    providerConfig: z.record(z.unknown()).optional()
});
/**
 * MergeableConfigItem - config item that supports partial merging
 * Both SingleConfig and CollectionConfig can have _mergePartial during the build phase
 */
export const mergeableConfigItemSchema = z.object({
    key: z.string(),
    _mergePartial: z.string().optional(),
}).passthrough();
/**
 * WorkspaceConfig - complete workspace configuration
 * Extends WorkspaceDetails with dynamics, path, and key
 * Note: dynamics uses a permissive schema since configs may have varied structures
 */
export const workspaceConfigSchema = workspaceDetailsBaseSchema.extend({
    // Dynamics can have varied structures, use permissive validation
    dynamics: z.array(mergeableConfigItemSchema).optional(),
    path: z.string().optional(),
    key: z.string().optional(),
});
// Workspace details with auto-migration from old Hugo-specific format
// Using preprocess to handle both old (hugover) and new (ssgType + ssgVersion) formats
export const workspaceDetailsSchema = z.preprocess((data) => {
    if (!data || !(data instanceof Object))
        return data;
    const ssgType = 'ssgType' in data ? data.ssgType : 'hugo';
    // If old format with hugover, convert to new format
    if ('hugover' in data) {
        const transformed = {
            ...data,
            ssgType: ssgType,
            ssgVersion: data.hugover,
            hugover: undefined // Remove old field
        };
        return transformed;
    }
    // If missing both hugover and ssgVersion, add defaults (very old configs)
    if (!('ssgVersion' in data) && !('hugover' in data)) {
        const transformed = {
            ...data,
            ssgType: ssgType,
            ssgVersion: 'v0.100.2' // Default version for old configs without version info
        };
        return transformed;
    }
    // If has ssgVersion but missing ssgType, default to hugo
    if ('ssgVersion' in data && !('ssgType' in data)) {
        const transformed = {
            ...data,
            ssgType: 'hugo'
        };
        return transformed;
    }
    return data;
}, workspaceDetailsBaseSchema);
export const configurationsSchema = z.object({
    sites: z.array(siteConfigSchema)
});
export const userPreferencesSchema = z.object({
    dataFolder: z.string().optional(),
    interfaceStyle: z.union([z.literal('quiqr10-dark'), z.literal('quiqr10-light')]).default('quiqr10-light'),
    sitesListingView: z.string().optional(),
    libraryView: z.string().optional(),
    systemGitBinPath: z.string().optional(),
    customOpenInCommand: z.string().optional(),
    showSplashAtStartup: z.boolean().optional(),
    applicationRole: z.string().optional(),
}).passthrough(); // TODO fix nested types
// Schema for the full application config object (global.pogoconf in backend)
export const appConfigSchema = z.object({
    lastOpenedSite: z.object({
        siteKey: z.string().nullable(),
        workspaceKey: z.string().nullable(),
        sitePath: z.string().nullable()
    }),
    prefs: userPreferencesSchema,
    lastOpenedPublishTargetForSite: z.record(z.string()),
    skipWelcomeScreen: z.boolean(),
    experimentalFeatures: z.boolean(),
    disablePartialCache: z.boolean(),
    devDisableAutoHugoServe: z.boolean(),
    hugoServeDraftMode: z.boolean(),
    sitesListingView: z.string(),
    currentUsername: z.string().nullable()
});
export const grayMatterParseResultSchema = z.object({
    content: z.string(),
    data: z.record(z.any()),
    excerpt: z.string(),
    isEmpty: z.boolean(),
    orig: z.record(z.string(), z.number())
});
// ============================================================================
// Unified Configuration Architecture
// ============================================================================
// Simplified 2-layer config: App Defaults < User Preferences
// See: openspec/changes/unify-configuration-architecture/
/**
 * Instance-level settings schema
 * Settings that apply to the entire Quiqr instance
 * File: instance_settings.json
 */
export const instanceSettingsSchema = z.object({
    // Storage configuration
    storage: z.object({
        type: z.enum(['fs', 's3']).default('fs'),
        dataFolder: z.string().default('~/Quiqr'),
    }).default({ type: 'fs', dataFolder: '~/Quiqr' }),
    // Git configuration
    git: z.object({
        binaryPath: z.string().nullable().optional(),
    }).default({}),
    // Logging configuration
    logging: z.object({
        retention: z.number().min(0).max(365).default(30),
        logLevel: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
    }).default({ retention: 30, logLevel: 'info' }),
    // Feature flags and experimental features
    experimentalFeatures: z.boolean().default(false),
    // Development settings
    dev: z.object({
        disablePartialCache: z.boolean().default(false),
    }).default({ disablePartialCache: false }),
    // Hugo-specific settings
    hugo: z.object({
        serveDraftMode: z.boolean().default(false),
        disableAutoHugoServe: z.boolean().default(false),
    }).default({ serveDraftMode: false, disableAutoHugoServe: false }),
    // Preview configuration
    preview: z.object({
        enabled: z.boolean().default(true),
        baseUrl: z.string().default('http://localhost:13131'),
    }).default({ enabled: true, baseUrl: 'http://localhost:13131' }),
    // Global build action variables (machine-specific overrides for %VAR% substitution)
    variables: z.record(z.string(), z.string()).default({}),
    // Authentication configuration (standalone mode only)
    auth: authConfigSchema.optional(),
});
/**
 * User-level configuration schema
 * Contains user preferences and user-specific state
 * File: user_prefs_ELECTRON.json (Electron edition) or user_prefs_[userId].json
 */
export const userConfigSchema = z.object({
    // User identifier ('ELECTRON' for Electron single-user edition)
    userId: z.string().default('ELECTRON'),
    // User UI preferences (overrides app defaults)
    preferences: z.object({
        interfaceStyle: z.union([z.literal('quiqr10-dark'), z.literal('quiqr10-light')]).optional(),
        customOpenCommand: z.string().optional(),
    }).default({}),
    // Last opened site state
    lastOpenedSite: z.object({
        siteKey: z.string().nullable(),
        workspaceKey: z.string().nullable(),
        sitePath: z.string().nullable(),
    }).default({ siteKey: null, workspaceKey: null, sitePath: null }),
    // Last opened publish target per site
    lastOpenedPublishTargetForSite: z.record(z.string()).default({}),
    // UI state flags
    skipWelcomeScreen: z.boolean().default(false),
    sitesListingView: z.string().default('all'),
});
/**
 * Site-level settings schema (FUTURE - Not currently used)
 * Settings specific to a single site
 * Deferred to future multi-user/multi-site changes
 */
export const siteSettingsSchema = z.object({
    // Site identifier
    siteKey: z.string(),
    // Site-specific preferences (future use)
    settings: z.record(z.unknown()).default({}),
    // Last publish timestamp
    lastPublish: z.number().optional(),
    // Publish status
    publishStatus: z.number().int().min(0).max(8).optional(),
});
/**
 * Configuration layer enum for precedence tracking
 * Simplified to 2 layers
 */
export const configLayerSchema = z.enum([
    'app-default',
    'user',
]);
/**
 * Configuration property metadata schema
 * Used for about:config style property inspection
 */
export const configPropertyMetadataSchema = z.object({
    // Dot-notation path to the property
    path: z.string(),
    // Current effective value
    value: z.unknown(),
    // Which layer provided this value
    source: configLayerSchema,
    // Type of the value
    type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
    // Optional description
    description: z.string().optional(),
});
/**
 * Environment variable mapping schema
 * Maps QUIQR_* env vars to config paths
 */
export const envVarMappingSchema = z.object({
    // Environment variable name (without QUIQR_ prefix)
    envVar: z.string(),
    // Dot-notation config path
    configPath: z.string(),
    // Value transformation
    transform: z.enum(['string', 'number', 'boolean', 'json']).default('string'),
});
/**
 * Standard environment variable mappings
 * Format: QUIQR_<SECTION>_<KEY> maps to instance settings paths
 * Environment variables can override instance_settings.json values
 */
export const standardEnvMappings = [
    { envVar: 'STORAGE_TYPE', configPath: 'storage.type', transform: 'string' },
    { envVar: 'STORAGE_DATAFOLDER', configPath: 'storage.dataFolder', transform: 'string' },
    { envVar: 'GIT_BINARY_PATH', configPath: 'git.binaryPath', transform: 'string' },
    { envVar: 'LOGGING_RETENTION', configPath: 'logging.retention', transform: 'number' },
    { envVar: 'LOGGING_LEVEL', configPath: 'logging.logLevel', transform: 'string' },
    { envVar: 'EXPERIMENTAL_FEATURES', configPath: 'experimentalFeatures', transform: 'boolean' },
    { envVar: 'DEV_DISABLE_PARTIAL_CACHE', configPath: 'dev.disablePartialCache', transform: 'boolean' },
    { envVar: 'HUGO_SERVE_DRAFT_MODE', configPath: 'hugo.serveDraftMode', transform: 'boolean' },
    { envVar: 'HUGO_DISABLE_AUTO_SERVE', configPath: 'hugo.disableAutoHugoServe', transform: 'boolean' },
    { envVar: 'AUTH_ENABLED', configPath: 'auth.enabled', transform: 'boolean' },
    { envVar: 'AUTH_SESSION_SECRET', configPath: 'auth.session.secret', transform: 'string' },
];
/**
 * Legacy config mapping (DEPRECATED - No migration)
 * Kept for reference only. Migration removed per design decision.
 */
export const legacyToUnifiedMapping = {
    // Reference mapping (not used in code)
    // Old quiqr-app-config.json → new structure
    'experimentalFeatures': 'instance_settings.json: experimentalFeatures',
    'disablePartialCache': 'instance_settings.json: dev.disablePartialCache',
    'devDisableAutoHugoServe': 'instance_settings.json: hugo.disableAutoHugoServe',
    'hugoServeDraftMode': 'instance_settings.json: hugo.serveDraftMode',
    'prefs.dataFolder': 'instance_settings.json: storage.dataFolder',
    'prefs.interfaceStyle': 'user_prefs_ELECTRON.json: preferences.interfaceStyle',
    'lastOpenedSite': 'user_prefs_ELECTRON.json: lastOpenedSite',
    'lastOpenedPublishTargetForSite': 'user_prefs_ELECTRON.json: lastOpenedPublishTargetForSite',
    'skipWelcomeScreen': 'user_prefs_ELECTRON.json: skipWelcomeScreen',
    'sitesListingView': 'user_prefs_ELECTRON.json: sitesListingView',
};
